import {
  EMPTY_LOCATION_GEO_STATE,
  enrichLocationFromApi,
} from "../../geo/lib/locationGeo";
import {
  buildCooperationOfferPayload,
  emptyCooperationFormFields,
  hydrateCooperationFormFields,
} from "../../realty/lib/cooperationOffer";

export const SUBTYPE_BY_PROPERTY_TYPE = {
  apartment: "residential",
  house: "residential",
  land: "land",
  commercial: "commercial",
  industrial: "commercial",
};

export const WIZARD_STEPS = [
  { n: 1, label: "Тип і базове" },
  { n: 2, label: "Локація" },
  { n: 3, label: "Характеристики" },
  { n: 4, label: "Особливості" },
  { n: 5, label: "Фото" },
  { n: 6, label: "Опис" },
];

export const TOTAL_WIZARD_STEPS = WIZARD_STEPS.length;

export const DRAFT_QUERY_PARAM = "draft";

const CORE_BOOTSTRAP_KEYS = [
  "title",
  "property_type",
  "deal_type",
  "price",
  "currency",
  "has_recovery_program",
  "has_housing_program",
];

const CORE_FULL_KEYS = [
  ...CORE_BOOTSTRAP_KEYS,
  "description_public",
  "description_private",
  "video_url",
];

const RESIDENTIAL_KEYS = [
  "total_area_m2",
  "living_area_m2",
  "kitchen_area_m2",
  "rooms",
  "bathrooms",
  "floor",
  "total_floors",
  "year_built",
  "land_area_m2",
  "walls",
  "heating",
  "condition",
  "furniture",
  "residential_complex",
  "has_balcony",
  "has_parking",
];

const LAND_KEYS = [
  "area_m2",
  "purpose",
  "has_road_access",
  "has_utilities",
];

const COMMERCIAL_KEYS = [
  "total_area_m2",
  "floor",
  "suitable_for",
  "walls",
  "heating",
  "condition",
  "furniture",
];

export const buildInitialForm = () => ({
  core: {
    property_type: "apartment",
    deal_type: "sale",
    title: "",
    price: "",
    currency: "USD",
    has_housing_program: false,
    has_recovery_program: false,
    description_public: "",
    description_private: "",
    video_url: "",
    ...emptyCooperationFormFields(),
  },
  location: {
    latitude: "",
    longitude: "",
    ...EMPTY_LOCATION_GEO_STATE,
  },
  residential: {
    total_area_m2: "",
    living_area_m2: "",
    kitchen_area_m2: "",
    rooms: "",
    bathrooms: "",
    floor: "",
    total_floors: "",
    year_built: "",
    land_area_m2: "",
    walls: "",
    heating: "",
    condition: "",
    furniture: "",
    residential_complex: "",
    has_balcony: false,
    has_parking: false,
  },
  land: {
    area_m2: "",
    purpose: "",
    has_road_access: false,
    has_utilities: false,
  },
  commercial: {
    total_area_m2: "",
    floor: "",
    suitable_for: "",
    walls: "",
    heating: "",
    condition: "",
    furniture: "",
  },
});

const pickKeys = (source, keys) => {
  const out = {};
  if (!source) {
    for (const k of keys) {
      if (k.startsWith("has_")) out[k] = false;
      else out[k] = "";
    }
    return out;
  }
  for (const k of keys) {
    const v = source[k];
    if (v === null || v === undefined) {
      out[k] = k.startsWith("has_") ? false : "";
    } else {
      out[k] = v;
    }
  }
  return out;
};

export const coerceNullable = (value) => {
  if (value === "" || value === undefined) return null;
  return value;
};

export const isFilled = (value) => String(value ?? "").trim() !== "";

export const buildDraftBootstrapPayload = (core) => ({
  title: core.title,
  property_type: core.property_type,
  deal_type: core.deal_type,
  price: core.price,
  currency: core.currency,
  has_recovery_program: Boolean(core.has_recovery_program),
  has_housing_program: Boolean(core.has_housing_program),
});

export const buildCorePayload = (core) => ({
  title: core.title,
  description_public: core.description_public,
  description_private: core.description_private,
  property_type: core.property_type,
  deal_type: core.deal_type,
  price: core.price,
  currency: core.currency,
  has_recovery_program: Boolean(core.has_recovery_program),
  has_housing_program: Boolean(core.has_housing_program),
  video_url: core.video_url || "",
  cooperation_offer: buildCooperationOfferPayload(core),
});

export const buildLocationPayload = (location, buildLocationApiPayload) =>
  buildLocationApiPayload(location, coerceNullable);

export const buildResidentialPayload = (res) => ({
  total_area_m2: res.total_area_m2,
  living_area_m2: coerceNullable(res.living_area_m2),
  kitchen_area_m2: coerceNullable(res.kitchen_area_m2),
  rooms: coerceNullable(res.rooms),
  bathrooms: coerceNullable(res.bathrooms),
  floor: coerceNullable(res.floor),
  total_floors: coerceNullable(res.total_floors),
  year_built: coerceNullable(res.year_built),
  land_area_m2: coerceNullable(res.land_area_m2),
  walls: res.walls || "",
  heating: res.heating || "",
  condition: res.condition || "",
  furniture: res.furniture || "",
  residential_complex: res.residential_complex || "",
  has_balcony: Boolean(res.has_balcony),
  has_parking: Boolean(res.has_parking),
});

export const buildLandPayload = (land) => ({
  area_m2: land.area_m2,
  purpose: land.purpose,
  has_road_access: Boolean(land.has_road_access),
  has_utilities: Boolean(land.has_utilities),
});

export const buildCommercialPayload = (com) => ({
  total_area_m2: com.total_area_m2,
  floor: coerceNullable(com.floor),
  suitable_for: com.suitable_for,
  walls: com.walls || "",
  heating: com.heating || "",
  condition: com.condition || "",
  furniture: com.furniture || "",
});

export const locationReady = (location, locationCanonicalReady) =>
  locationCanonicalReady(location);

export const subtypeReady = (propertyType, form) => {
  const subtype = SUBTYPE_BY_PROPERTY_TYPE[propertyType];
  if (subtype === "residential") return isFilled(form.residential.total_area_m2);
  if (subtype === "land") {
    return isFilled(form.land.area_m2) && isFilled(form.land.purpose);
  }
  if (subtype === "commercial") {
    return (
      isFilled(form.commercial.total_area_m2)
      && isFilled(form.commercial.suitable_for)
    );
  }
  return false;
};

export const validateStep1ForDraft = (core) => {
  const issues = {};
  if (!isFilled(core.title)) {
    issues.title = "Вкажіть назву оголошення.";
  }
  const price = core.price === "" ? null : Number(core.price);
  if (price === null || !Number.isFinite(price) || price <= 0) {
    issues.price = "Ціна має бути більшою за 0.";
  }
  return issues;
};

export const formFromPropertyDetail = (property) => ({
  core: {
    ...pickKeys(property, CORE_FULL_KEYS),
    ...hydrateCooperationFormFields(
      property && property.cooperation_offer,
    ),
  },
  location: enrichLocationFromApi(property.location),
  residential: pickKeys(property.residential, RESIDENTIAL_KEYS),
  land: pickKeys(property.land, LAND_KEYS),
  commercial: pickKeys(property.commercial, COMMERCIAL_KEYS),
});

export const featureIdsFromProperty = (property) => {
  const features = (property && property.features) || [];
  return new Set(features.map((f) => f.id));
};

export const resolveResumeStep = (property) => {
  if (!property) return 1;
  if (!property.location) return 2;
  const subtype = SUBTYPE_BY_PROPERTY_TYPE[property.property_type];
  if (subtype === "residential" && !property.residential) return 3;
  if (subtype === "land" && !property.land) return 3;
  if (subtype === "commercial" && !property.commercial) return 3;
  return 4;
};

export const parseDraftIdParam = (raw) => {
  if (!raw || !/^\d+$/.test(raw)) return null;
  return Number(raw);
};

export const STEP_STATE = {
  ACTIVE: "active",
  COMPLETE: "complete",
  INCOMPLETE: "incomplete",
  UPCOMING: "upcoming",
};

export const STEP_STATE_LABELS = {
  active: "поточний крок",
  complete: "завершено",
  incomplete: "незавершено",
  upcoming: "наступний",
};

export const collectSpecsFieldIssues = (form) => {
  const issues = {};
  if (!form) return issues;
  const num = (v) =>
    v === "" || v === null || v === undefined ? null : Number(v);
  const isBad = (n) => n !== null && Number.isFinite(n) && n <= 0;

  const subtype = SUBTYPE_BY_PROPERTY_TYPE[form.core?.property_type];
  if (subtype === "residential") {
    const res = form.residential || {};
    if (isBad(num(res.total_area_m2))) {
      issues.total_area_m2 = "Площа має бути більшою за 0.";
    }
    if (isBad(num(res.rooms))) {
      issues.rooms = "Кількість кімнат має бути більшою за 0.";
    }
    const totalFloors = num(res.total_floors);
    if (isBad(totalFloors)) {
      issues.total_floors = "Поверховість має бути більшою за 0.";
    }
    const floor = num(res.floor);
    if (
      floor !== null && Number.isFinite(floor)
      && totalFloors !== null && Number.isFinite(totalFloors)
      && floor > totalFloors
    ) {
      issues.floor = "Поверх не може бути більшим за поверховість будинку.";
    }
    const totalArea = num(res.total_area_m2);
    const livingArea = num(res.living_area_m2);
    if (
      livingArea !== null && Number.isFinite(livingArea)
      && totalArea !== null && Number.isFinite(totalArea)
      && livingArea > totalArea
    ) {
      issues.living_area_m2 =
        "Житлова площа не може бути більшою за загальну площу.";
    }
    const kitchenArea = num(res.kitchen_area_m2);
    if (
      kitchenArea !== null && Number.isFinite(kitchenArea)
      && totalArea !== null && Number.isFinite(totalArea)
      && kitchenArea > totalArea
    ) {
      issues.kitchen_area_m2 =
        "Площа кухні не може бути більшою за загальну площу.";
    }
  } else if (subtype === "land") {
    if (isBad(num((form.land || {}).area_m2))) {
      issues.area_m2 = "Площа має бути більшою за 0.";
    }
  } else if (subtype === "commercial") {
    if (isBad(num((form.commercial || {}).total_area_m2))) {
      issues.total_area_m2 = "Площа має бути більшою за 0.";
    }
  }
  return issues;
};

export const isStep1Complete = (form) =>
  Object.keys(validateStep1ForDraft(form?.core || {})).length === 0;

export const isStep2Complete = (form, locationCanonicalReadyFn) =>
  locationReady(form?.location || {}, locationCanonicalReadyFn);

export const isStep3Complete = (form) =>
  subtypeReady(form?.core?.property_type, form)
  && Object.keys(collectSpecsFieldIssues(form)).length === 0;

export const visitedStepsFromResume = (resumeStep) => {
  const visited = new Set();
  const end = Math.max(1, Math.min(Number(resumeStep) || 1, TOTAL_WIZARD_STEPS));
  for (let n = 1; n <= end; n += 1) {
    visited.add(n);
  }
  return visited;
};

export const markStepVisited = (visitedSteps, stepNumber) => {
  const next = new Set(visitedSteps || []);
  if (stepNumber >= 1 && stepNumber <= TOTAL_WIZARD_STEPS) {
    next.add(stepNumber);
  }
  return next;
};

export const featureIdSetsEqual = (a, b) => {
  const left = a || new Set();
  const right = b || new Set();
  if (left.size !== right.size) return false;
  for (const id of left) {
    if (!right.has(id)) return false;
  }
  return true;
};

export const deriveWizardStepState = ({
  stepNumber,
  currentStep,
  visitedSteps,
  form,
  locationCanonicalReadyFn,
  step4Acknowledged = false,
  step5Acknowledged = false,
  selectedFeatureIds,
  savedFeatureIds,
  hasPendingUploads = false,
}) => {
  if (stepNumber === currentStep) return STEP_STATE.ACTIVE;
  if (!visitedSteps || !visitedSteps.has(stepNumber)) {
    return STEP_STATE.UPCOMING;
  }

  let complete = false;
  if (stepNumber === 1) {
    complete = isStep1Complete(form);
  } else if (stepNumber === 2) {
    complete = isStep2Complete(form, locationCanonicalReadyFn);
  } else if (stepNumber === 3) {
    complete = isStep3Complete(form);
  } else if (stepNumber === 4) {
    complete =
      Boolean(step4Acknowledged)
      && featureIdSetsEqual(selectedFeatureIds, savedFeatureIds);
  } else if (stepNumber === 5) {
    complete = Boolean(step5Acknowledged) && !hasPendingUploads;
  } else if (stepNumber === 6) {
    complete = false;
  }

  return complete ? STEP_STATE.COMPLETE : STEP_STATE.INCOMPLETE;
};

export const deriveWizardStepStates = (args) => {
  const byNumber = {};
  for (const step of WIZARD_STEPS) {
    byNumber[step.n] = deriveWizardStepState({
      ...args,
      stepNumber: step.n,
    });
  }
  return byNumber;
};
